/**
 * StaffManagement
 * CCH Care — Phase 2.5+
 * Last Modified: 2026-05-24
 *
 * Invite, edit, deactivate, reactivate, and reset passwords for staff.
 * Edit panel slides in from the right when a staff row is tapped.
 *
 * Changes (2026-05-24) — Reactivate support:
 * - "Show inactive" toggle in Current Staff header.
 * - Inactive rows render with muted bg + opacity + INACTIVE pill.
 * - Edit panel swaps Deactivate → Reactivate (green) for inactive members.
 * - Reset Password hidden for inactive members (login is gated anyway).
 * - Always fetches with include_inactive=1; toggle filters client-side
 *   so counts stay accurate and toggle is instant (no refetch flicker).
 *
 * Changes (2026-04-21) — Palette tokenization:
 * - ROLE_COLORS removed. Now imports ROLE_BADGES from constants.js
 *   with dedicated .color / .alpha pairs.
 * - CredentialsCard + reset banner: amber hexes → COLORS.credential* tokens.
 *
 * Changes (2026-04-20) — Dark mode + ESLint pass:
 * - Header / form cards / panel / modal backgrounds → COLORS.surface.
 * - Error banners → COLORS.dangerLight + COLORS.danger.
 * - Field/Select: background + color tokens added.
 * - fetchStaff wrapped in useCallback, effect dep fixed.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as adminApi from '../../../api/admin';
import { COLORS, ROLE_BADGES } from '../../../config/constants';

const ROLE_OPTIONS = [
  { value: 'CNA',             label: 'CNA' },
  { value: 'Care Supervisor', label: 'Care Supervisor' },
  { value: 'Care Admin',      label: 'Care Admin' },
  { value: 'Nurse',           label: 'Nurse' },
];

/** Decodes HTML entities (e.g. &#039; → ') returned from the API. */
function decodeHtml(str) {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

export default function StaffManagement() {
  const navigate = useNavigate();
  const [staff, setStaff]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [formData, setFormData]       = useState({});
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Edit panel state
  const [editingMember, setEditingMember]       = useState(null);
  const [editForm, setEditForm]                 = useState({});
  const [editSaving, setEditSaving]             = useState(false);
  const [editError, setEditError]               = useState(null);
  const [resetCredentials, setResetCredentials] = useState(null);
  const [confirmAction, setConfirmAction]       = useState(null);

  const fetchStaff = useCallback(() => {
    // Always fetch all (active + inactive). Filtering is client-side so the toggle
    // is instant and counts stay accurate.
    adminApi.getStaff({ includeInactive: true }).then(d => {
      if (d.success) setStaff(d.staff);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const update     = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));
  const updateEdit = (key, val) => setEditForm(prev => ({ ...prev, [key]: val }));

  // Derived counts + visible list
  const activeCount   = staff.filter(s => !!+s.is_active).length;
  const inactiveCount = staff.length - activeCount;
  const visibleStaff  = showInactive ? staff : staff.filter(s => !!+s.is_active);

  // ── Invite ───────────────────────────────────────────────
  const handleInvite = async () => {
    setError(null);
    setCredentials(null);
    if (!formData.first_name?.trim() || !formData.last_name?.trim() || !formData.email?.trim() || !formData.role) {
      setError('Please fill in all required fields.');
      return;
    }
    setSaving(true);
    try {
      const result = await adminApi.inviteStaff({
        first_name: formData.first_name,
        last_name:  formData.last_name,
        email:      formData.email,
        role:       formData.role,
        phone:      formData.phone || '',
      });
      if (result.success !== false) {
        if (result.temp_password) {
          setCredentials({ name: formData.first_name + ' ' + formData.last_name, email: formData.email, password: result.temp_password, role: formData.role });
        }
        setFormData({});
        fetchStaff();
      } else {
        setError(result.error || 'Failed to invite staff member.');
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  // ── Open edit panel ──────────────────────────────────────
  function openEdit(member) {
    setEditingMember(member);
    setEditForm({
      first_name: decodeHtml(member.first_name),
      last_name:  decodeHtml(member.last_name),
      phone:      member.phone || '',
      role:       member.role,
    });
    setEditError(null);
    setResetCredentials(null);
    setConfirmAction(null);
  }

  function closeEdit() {
    setEditingMember(null);
    setEditForm({});
    setEditError(null);
    setResetCredentials(null);
    setConfirmAction(null);
  }

  // ── Save edits ───────────────────────────────────────────
  const handleEditSave = async () => {
    if (!editForm.first_name?.trim() || !editForm.last_name?.trim() || !editForm.role) {
      setEditError('First name, last name, and role are required.');
      return;
    }

    const roleChanged = editForm.role !== editingMember.role;
    if (roleChanged) {
      setConfirmAction({
        type: 'role_change',
        label: `Change role from "${editingMember.role}" to "${editForm.role}"?`,
        onConfirm: () => doEditSave(),
      });
      return;
    }
    doEditSave();
  };

  const doEditSave = async () => {
    setConfirmAction(null);
    setEditSaving(true);
    setEditError(null);
    try {
      const result = await adminApi.updateStaff({
        membership_id: editingMember.membership_id,
        first_name:    editForm.first_name,
        last_name:     editForm.last_name,
        phone:         editForm.phone,
        role:          editForm.role,
      });
      if (result.success !== false) {
        fetchStaff();
        closeEdit();
      } else {
        setEditError(result.error || 'Failed to save changes.');
      }
    } catch (err) {
      setEditError(err?.response?.data?.error || err.message || 'An error occurred.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Reset password ───────────────────────────────────────
  const handleResetPassword = () => {
    const fullName = `${decodeHtml(editingMember.first_name)} ${decodeHtml(editingMember.last_name)}`;
    setConfirmAction({
      type: 'reset_password',
      label: `Reset password for ${fullName}? They will receive a new temporary password.`,
      onConfirm: async () => {
        setConfirmAction(null);
        setEditSaving(true);
        setEditError(null);
        try {
          const result = await adminApi.resetStaffPassword(editingMember.membership_id);
          if (result.success !== false) {
            setResetCredentials({ name: fullName, email: editingMember.email, password: result.temp_password });
          } else {
            setEditError(result.error || 'Failed to reset password.');
          }
        } catch (err) {
          setEditError(err?.response?.data?.error || err.message || 'An error occurred.');
        } finally {
          setEditSaving(false);
        }
      },
    });
  };

  // ── Deactivate ───────────────────────────────────────────
  const handleDeactivate = () => {
    const fullName = `${decodeHtml(editingMember.first_name)} ${decodeHtml(editingMember.last_name)}`;
    setConfirmAction({
      type: 'deactivate',
      label: `Deactivate ${fullName}? They will lose access immediately.`,
      onConfirm: async () => {
        setConfirmAction(null);
        setEditSaving(true);
        setEditError(null);
        try {
          const result = await adminApi.deactivateStaff(editingMember.membership_id);
          if (result.success !== false) {
            fetchStaff();
            closeEdit();
          } else {
            setEditError(result.error || 'Failed to deactivate.');
          }
        } catch (err) {
          setEditError(err?.response?.data?.error || err.message || 'An error occurred.');
        } finally {
          setEditSaving(false);
        }
      },
    });
  };

  // ── Reactivate ───────────────────────────────────────────
  const handleReactivate = () => {
    const fullName = `${decodeHtml(editingMember.first_name)} ${decodeHtml(editingMember.last_name)}`;
    setConfirmAction({
      type: 'reactivate',
      label: `Reactivate ${fullName}? They will regain access immediately.`,
      onConfirm: async () => {
        setConfirmAction(null);
        setEditSaving(true);
        setEditError(null);
        try {
          const result = await adminApi.reactivateStaff(editingMember.membership_id);
          if (result.success !== false) {
            fetchStaff();
            closeEdit();
          } else {
            setEditError(result.error || 'Failed to reactivate.');
          }
        } catch (err) {
          setEditError(err?.response?.data?.error || err.message || 'An error occurred.');
        } finally {
          setEditSaving(false);
        }
      },
    });
  };

  return (
    <div style={{ paddingBottom: 80 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', background: COLORS.surface, borderBottom: '1px solid ' + COLORS.border, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/dashboard/more')} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: COLORS.primary, padding: '4px 8px' }}>← Back</button>
        <span style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>Staff Management</span>
      </div>

      <div style={{ padding: 16 }}>
        {/* Invite Form */}
        <div style={{ background: COLORS.surface, borderRadius: 12, padding: 20, border: '1px solid ' + COLORS.border, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, color: COLORS.text }}>Invite Staff Member</h3>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: COLORS.textMuted }}>
            Create a login for a new CNA or supervisor. They&apos;ll receive temporary credentials to log in and set up 2FA.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field id="invite-first-name" label="First Name *" value={formData.first_name || ''} onChange={v => update('first_name', v)} />
              <Field id="invite-last-name" label="Last Name *" value={formData.last_name || ''} onChange={v => update('last_name', v)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Field id="invite-email" label="Email *" type="email" value={formData.email || ''} onChange={v => update('email', v)} placeholder="cna@facility.com" />
              <Select id="invite-role" label="Role *" value={formData.role || ''} onChange={v => update('role', v)} options={ROLE_OPTIONS} placeholder="Select role" />
            </div>
            <Field id="invite-phone" label="Phone (optional)" value={formData.phone || ''} onChange={v => update('phone', v)} placeholder="555-0100" />

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, fontSize: 13, color: COLORS.danger, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{error}</span>
                <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 14 }}>✕</button>
              </div>
            )}

            <button onClick={handleInvite} disabled={saving} style={{
              padding: '12px 24px', borderRadius: 8, border: 'none',
              background: COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600,
              cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1, alignSelf: 'flex-start',
            }}>
              {saving ? 'Inviting...' : 'Invite Staff Member'}
            </button>
          </div>
        </div>

        {/* Credentials Display */}
        {credentials && <CredentialsCard credentials={credentials} onDismiss={() => setCredentials(null)} />}

        {/* Staff List */}
        <div style={{ background: COLORS.surface, borderRadius: 12, padding: 20, border: '1px solid ' + COLORS.border }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>
              Current Staff ({activeCount}{inactiveCount > 0 && showInactive ? ` · ${inactiveCount} inactive` : ''})
            </h3>
            {inactiveCount > 0 && (
              <button
                onClick={() => setShowInactive(v => !v)}
                style={{
                  padding: '5px 10px', borderRadius: 6,
                  border: `1px solid ${showInactive ? COLORS.accent : COLORS.border}`,
                  background: showInactive ? (COLORS.accentLight || COLORS.surface) : COLORS.surface,
                  color: showInactive ? COLORS.accent : COLORS.textMuted,
                  fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {showInactive ? `✓ Showing inactive` : `Show inactive (${inactiveCount})`}
              </button>
            )}
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: COLORS.textMuted }}>Tap a staff member to edit their details.</p>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted }}>Loading...</div>
          ) : visibleStaff.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted }}>No staff members yet. Invite your first CNA above.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {visibleStaff.map(s => {
                const firstName = decodeHtml(s.first_name);
                const lastName  = decodeHtml(s.last_name);
                const roleBadge = ROLE_BADGES[s.role];
                const roleColor = roleBadge?.color || COLORS.primary;
                const roleAlpha = roleBadge?.alpha || COLORS.primaryAlpha;
                const isActive  = !!+s.is_active;
                return (
                  <div
                    key={s.membership_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openEdit(s)}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openEdit(s)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px',
                      background: isActive ? COLORS.bg : `${COLORS.border}40`,
                      borderRadius: 8, cursor: 'pointer',
                      border: '1px solid transparent',
                      transition: 'border-color 0.15s',
                      opacity: isActive ? 1 : 0.78,
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.primary}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%',
                      background: roleAlpha,
                      color: roleColor,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700, flexShrink: 0,
                      filter: isActive ? 'none' : 'grayscale(0.5)',
                    }}>
                      {(firstName[0] || '') + (lastName[0] || '')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text }}>{firstName} {lastName}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>{s.email}</div>
                    </div>
                    {!isActive && (
                      <span style={{
                        padding: '3px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                        background: COLORS.dangerLight, color: COLORS.danger,
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                      }}>
                        Inactive
                      </span>
                    )}
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      background: roleAlpha,
                      color: roleColor,
                    }}>
                      {s.role}
                    </span>
                    <span style={{ fontSize: 14, color: COLORS.textMuted }}>›</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Edit Panel (slide-in) ── */}
      {editingMember && (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={closeEdit}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && closeEdit()}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }}
          />

          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 420,
            background: COLORS.surface, zIndex: 201, display: 'flex', flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            animation: 'slideInRight 0.25s ease-out',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + COLORS.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                  Edit Staff Member
                  {!+editingMember.is_active && (
                    <span style={{
                      padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                      background: COLORS.dangerLight, color: COLORS.danger,
                      textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                      Inactive
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted }}>{editingMember.email}</div>
              </div>
              <button onClick={closeEdit} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.textMuted, padding: '4px 8px' }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

              {resetCredentials && (
                <div style={{ background: COLORS.credentialBg, border: `2px solid ${COLORS.credentialBorder}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.credentialText, marginBottom: 8 }}>🔑 New Temporary Password</div>
                  <div style={{ fontSize: 13, color: COLORS.credentialText, marginBottom: 4 }}>Email: <strong style={{ fontFamily: 'monospace' }}>{resetCredentials.email}</strong></div>
                  <div style={{ fontSize: 13, color: COLORS.credentialText, marginBottom: 10 }}>Password: <strong style={{ fontFamily: 'monospace' }}>{resetCredentials.password}</strong></div>
                  <button onClick={() => {
                    const text = `CCH Care Login\nURL: ${window.location.origin}/login\nEmail: ${resetCredentials.email}\nPassword: ${resetCredentials.password}`;
                    navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard!'));
                  }} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${COLORS.credentialBorder}`, background: '#fff', color: COLORS.credentialText, fontSize: 12, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>
                    📋 Copy
                  </button>
                  <button onClick={() => setResetCredentials(null)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid ' + COLORS.border, background: '#fff', color: COLORS.textMuted, fontSize: 12, cursor: 'pointer' }}>
                    Dismiss
                  </button>
                  <p style={{ margin: '10px 0 0', fontSize: 11, color: COLORS.credentialTextHelper }}>⚠ Shown only once.</p>
                </div>
              )}

              {editError && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: COLORS.dangerLight, border: '1px solid ' + COLORS.danger, fontSize: 13, color: COLORS.danger, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{editError}</span>
                  <button onClick={() => setEditError(null)} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontSize: 14 }}>✕</button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field id="edit-first-name" label="First Name *" value={editForm.first_name || ''} onChange={v => updateEdit('first_name', v)} />
                  <Field id="edit-last-name" label="Last Name *" value={editForm.last_name || ''} onChange={v => updateEdit('last_name', v)} />
                </div>
                <Field id="edit-phone" label="Phone" value={editForm.phone || ''} onChange={v => updateEdit('phone', v)} placeholder="555-0100" />
                <Select id="edit-role" label="Role *" value={editForm.role || ''} onChange={v => updateEdit('role', v)} options={ROLE_OPTIONS} placeholder="Select role" />
              </div>

              <div style={{ borderTop: '1px solid ' + COLORS.border, margin: '20px 0' }} />

              {/* Account actions — Reset + Deactivate for active, Reactivate only for inactive */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>Account Actions</div>
                {!!+editingMember.is_active ? (
                  <>
                    <button
                      onClick={handleResetPassword}
                      disabled={editSaving}
                      style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid ' + COLORS.warning, background: COLORS.surface, color: COLORS.warning, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                    >
                      🔑 Reset Password
                    </button>
                    <button
                      onClick={handleDeactivate}
                      disabled={editSaving}
                      style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid ' + COLORS.danger, background: COLORS.surface, color: COLORS.danger, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                    >
                      🚫 Deactivate Account
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleReactivate}
                    disabled={editSaving}
                    style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid ' + COLORS.accent, background: COLORS.surface, color: COLORS.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}
                  >
                    🔄 Reactivate Account
                  </button>
                )}
              </div>
            </div>

            <div style={{ padding: '14px 20px', borderTop: '1px solid ' + COLORS.border, display: 'flex', gap: 8 }}>
              <button onClick={closeEdit} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editSaving} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: editSaving ? 'wait' : 'pointer', opacity: editSaving ? 0.7 : 1 }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Confirmation Dialog ── */}
      {confirmAction && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: COLORS.surface, borderRadius: 12, padding: 24, maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, color: COLORS.text }}>
              {confirmAction.type === 'deactivate'     ? '🚫 Deactivate Account' :
               confirmAction.type === 'reactivate'     ? '🔄 Reactivate Account' :
               confirmAction.type === 'reset_password' ? '🔑 Reset Password' :
               '✏️ Change Role'}
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: COLORS.textMuted, lineHeight: 1.5 }}>
              {confirmAction.label}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmAction(null)}
                style={{ padding: '9px 16px', borderRadius: 6, border: '1px solid ' + COLORS.border, background: COLORS.surface, color: COLORS.textMuted, fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmAction.onConfirm}
                style={{
                  padding: '9px 20px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: confirmAction.type === 'deactivate' ? COLORS.danger :
                              confirmAction.type === 'reactivate' ? COLORS.accent :
                              COLORS.primary,
                  color: '#fff',
                }}
              >
                {confirmAction.type === 'deactivate'     ? 'Deactivate' :
                 confirmAction.type === 'reactivate'     ? 'Reactivate' :
                 confirmAction.type === 'reset_password' ? 'Reset Password' :
                 'Change Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}

// ── Credentials Card ─────────────────────────────────────────
function CredentialsCard({ credentials, onDismiss }) {
  return (
    <div style={{ background: COLORS.credentialBg, border: `2px solid ${COLORS.credentialBorder}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>🔑</span>
        <h3 style={{ margin: 0, fontSize: 15, color: COLORS.credentialText }}>Login Credentials for {credentials.name}</h3>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 13, color: COLORS.credentialText }}>Share these credentials securely. The staff member will set up 2FA on first login.</p>
      <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: `1px solid ${COLORS.credentialInnerBorder}` }}>
        <CredRow label="Login URL" value={`${window.location.origin}/login`} mono={false} />
        <CredRow label="Email" value={credentials.email} mono />
        <CredRow label="Temporary Password" value={credentials.password} mono />
        <CredRow label="Role" value={credentials.role} mono={false} last />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button onClick={() => {
          const text = `CCH Care Login\nURL: ${window.location.origin}/login\nEmail: ${credentials.email}\nPassword: ${credentials.password}\nRole: ${credentials.role}`;
          navigator.clipboard.writeText(text).then(() => alert('Credentials copied to clipboard!'));
        }} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${COLORS.credentialBorder}`, background: '#fff', color: COLORS.credentialText, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          📋 Copy Credentials
        </button>
        <button onClick={onDismiss} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + COLORS.border, background: '#fff', color: COLORS.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Dismiss
        </button>
      </div>
      <p style={{ margin: '12px 0 0', fontSize: 11, color: COLORS.credentialTextHelper }}>⚠ This password is shown only once. If lost, the staff member will need a password reset.</p>
    </div>
  );
}

function CredRow({ label, value, mono, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, fontFamily: mono ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

// ── Form Components ──────────────────────────────────────────
function Field({ id, label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <input
        id={id}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid ' + COLORS.border, fontSize: 14, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', background: COLORS.surface, color: COLORS.text }}
        type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      />
    </div>
  );
}

function Select({ id, label, value, onChange, options, placeholder }) {
  return (
    <div>
      {label && <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>{label}</label>}
      <select
        id={id}
        value={value} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid ' + COLORS.border, fontSize: 14, background: COLORS.surface, color: COLORS.text, cursor: 'pointer', boxSizing: 'border-box' }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}